/**
 * YouTube Lazy Load JavaScript
 * 
 * This script handles the click-to-load functionality for embedded YouTube videos.
 * It replaces the thumbnail with the actual YouTube iframe only when the user clicks
 * the play button, improving page load performance.
 */

// Use an Intersection Observer to only initialize videos when they're visible
const initYouTubeLazyLoad = () => {
  // Find all YouTube lazy load containers
  const youtubeContainers = document.querySelectorAll('.youtube-lazy-container');
  
  if (!youtubeContainers.length) return;
  
  // Create intersection observer to only initialize videos when in viewport
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        setupYouTubeContainer(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, {
    rootMargin: '200px 0px', // Start loading when video is 200px from viewport
    threshold: 0.01
  });
  
  // Observe each YouTube container
  youtubeContainers.forEach(container => {
    observer.observe(container);
  });
};

// Set up a single YouTube container
const setupYouTubeContainer = (container) => {
  const playButton = container.querySelector('.youtube-lazy-play-button');
  const iframeContainer = container.querySelector('.youtube-lazy-iframe-container');
  const thumbnail = container.querySelector('.youtube-lazy-thumbnail');
  
  // Only proceed if all elements exist
  if (!playButton || !iframeContainer || !thumbnail) return;
  
  // Function to handle loading the video
  const loadYouTubeVideo = () => {
    const youtubeId = container.dataset.youtubeId;
    const videoTitle = container.dataset.videoTitle;
    const autoplay = container.dataset.autoplay === 'true' ? 1 : 0;
    const cookieConsent = container.dataset.cookieConsent === 'true';
    
    if (!youtubeId) return;
    
    // Use YouTube's privacy enhanced mode if no cookie consent
    const domain = cookieConsent ? 'www.youtube.com' : 'www.youtube-nocookie.com';
    const youtubeUrl = `https://${domain}/embed/${youtubeId}?autoplay=${autoplay}&rel=0`;
    
    // Create the iframe element
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', youtubeUrl);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '1');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('title', videoTitle || 'YouTube video');
    
    // Add the iframe to the container
    iframeContainer.appendChild(iframe);
    
    // Hide the thumbnail and show the iframe container
    thumbnail.style.display = 'none';
    iframeContainer.style.display = 'block';
    
    // Remove event listeners to prevent multiple calls
    playButton.removeEventListener('click', loadYouTubeVideo);
    playButton.removeEventListener('keydown', handleKeydown);
    
    // Add to browser history for better analytics tracking
    if (window.history && window.history.pushState) {
      const url = new URL(window.location.href);
      url.searchParams.set('video', youtubeId);
      window.history.pushState({}, '', url);
    }
  };
  
  // Handle keyboard interaction for accessibility
  const handleKeydown = (event) => {
    // Load the video when Enter or Space key is pressed
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      loadYouTubeVideo();
    }
  };
  
  // Add event listeners
  playButton.addEventListener('click', loadYouTubeVideo);
  playButton.addEventListener('keydown', handleKeydown);
};

// Check if the DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initYouTubeLazyLoad);
} else {
  initYouTubeLazyLoad();
}